// Root layout: providers for overlays + global chrome around the routed page.
import { lazy, Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useBodyClass } from '@/app/providers/bodyClass';
import { ShellCtx } from '@/app/providers/Shell';
import { LanguageGate } from '@/app/providers/Language';
import { AskBar } from '@/features/ai/AskBar';
import { OrcaProvider, useOrca } from '@/features/ai/OrcaProvider';
import { NewsSheetProvider, useNewsSheet } from '@/features/news/NewsSheet';
import { NoticesProvider } from '@/features/notices/NoticesProvider';
import { UrgentStack } from '@/features/notices/UrgentStack';
import { SheetProvider } from '@/components/ui/Sheet';
import { RouteSkeleton } from '@/components/ui/Skeleton';
import { useMock } from '@/dev/mockState';
import { CursorGlow, Grain } from './Ambient';
import { OfflineBanner, RouteBoundary } from './RouteBoundary';

const DevPanel = import.meta.env.DEV ? lazy(() => import('@/dev/DevPanel')) : null;
import { Drawer } from './Drawer';

/** Close overlays and scroll to top when the page (pathname) changes. */
function RouteEffects({ setNavOpen }: { setNavOpen: (v: boolean) => void }) {
  const { pathname } = useLocation();
  const { resetBar } = useOrca();
  const { hideNews } = useNewsSheet();
  useBodyClass('on-ai', pathname === '/ai');
  useEffect(() => {
    setNavOpen(false);
    resetBar();
    hideNews();
    window.scrollTo(0, 0);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export function AppShell() {
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  const mock = useMock();
  return (
    <ShellCtx.Provider value={{ navOpen, setNavOpen }}>
      <LanguageGate>
      <OrcaProvider>
        <NewsSheetProvider>
          <NoticesProvider>
            <SheetProvider>
              <Grain />
              <CursorGlow />
              <RouteEffects setNavOpen={setNavOpen} />
              <RouteBoundary path={pathname}>
                <Suspense key={pathname} fallback={<RouteSkeleton path={pathname} />}><Outlet /></Suspense>
              </RouteBoundary>
              <Drawer />
              <AskBar />
              <UrgentStack />
              <OfflineBanner forced={mock.data === 'offline'} />
              {DevPanel && <Suspense fallback={null}><DevPanel /></Suspense>}
            </SheetProvider>
          </NoticesProvider>
        </NewsSheetProvider>
      </OrcaProvider>
      </LanguageGate>
    </ShellCtx.Provider>
  );
}
